import React, { useState } from 'react';
import { Button, Card, Row, Col, Modal, Form, Input, message, Tag } from 'antd';
import {
  UserOutlined, BookOutlined, BarChartOutlined, MessageOutlined,
  RobotOutlined, ThunderboltOutlined, SafetyCertificateOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const { TextArea } = Input;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [form] = Form.useForm();

  const handleSubmitFeedback = async (values: any) => {
    try {
      await axios.post('/api/feedback', {
        name: values.name || 'Anonymous',
        email: values.email || '',
        feedback: values.feedback,
        rating: 5
      });
      message.success('Thank you for your feedback!');
      form.resetFields();
      setFeedbackVisible(false);
    } catch (error) {
      message.error('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <div>
      <Helmet>
        <title>Smart Test Statistics System – Online Exam &amp; Analytics Platform</title>
        <meta name="description" content="Create online exams, share test links with students, and get instant automated grading with detailed knowledge-point analytics. Free for teachers." />
        <link rel="canonical" href="/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Smart Test Statistics System – Online Exam &amp; Analytics Platform" />
        <meta property="og:description" content="Create online exams, share test links with students, and get instant automated grading with detailed knowledge-point analytics. Free for teachers." />
        <meta property="og:url" content="/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Smart Test Statistics System – Online Exam &amp; Analytics" />
        <meta name="twitter:description" content="Create online exams, share test links with students, and get instant automated grading with detailed knowledge-point analytics." />
      </Helmet>
      <div className="header">
        <h1>Smart Test Statistics System</h1>
        <p>Easily create, manage, and analyze online exams</p>
        <div style={{ marginTop: '20px' }}>
          <Button
            type="default"
            icon={<MessageOutlined />}
            onClick={() => setFeedbackVisible(true)}
            style={{ background: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
          >
            Share Your Feedback
          </Button>
        </div>
      </div>

      <div className="container">
        {/* 主功能入口 */}
        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} sm={12} md={8}>
            <Card hoverable className="card" cover={
              <div style={{ padding: '40px', background: '#f0f8ff', textAlign: 'center' }}>
                <UserOutlined style={{ fontSize: '64px', color: '#1890ff' }} />
              </div>
            }>
              <Card.Meta title="Teacher Portal" description="Upload tests, edit answers, mark knowledge points, view statistics" />
              <div style={{ marginTop: '20px' }}>
                <Button type="primary" size="large" block onClick={() => navigate('/teacher')}>
                  Enter Teacher Portal
                </Button>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Card hoverable className="card" cover={
              <div style={{ padding: '40px', background: '#f6ffed', textAlign: 'center' }}>
                <BookOutlined style={{ fontSize: '64px', color: '#52c41a' }} />
              </div>
            }>
              <Card.Meta title="Student Portal" description="Enter test link, fill in personal information, start answering" />
              <div style={{ marginTop: '20px' }}>
                <Button type="primary" size="large" block
                  onClick={() => { const id = prompt('Please enter the Test ID:'); if (id) navigate(`/test/${id}`); }}
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                  Start Test
                </Button>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Card hoverable className="card" cover={
              <div style={{ padding: '40px', background: '#fff7e6', textAlign: 'center' }}>
                <BarChartOutlined style={{ fontSize: '64px', color: '#fa8c16' }} />
              </div>
            }>
              <Card.Meta title="Statistics" description="View detailed student scores and statistical analysis" />
              <div style={{ marginTop: '20px' }}>
                <Button type="primary" size="large" block
                  onClick={() => { const id = prompt('Please enter the Test ID:'); if (id) navigate(`/statistics/${id}`); }}
                  style={{ background: '#fa8c16', borderColor: '#fa8c16' }}>
                  View Statistics
                </Button>
              </div>
            </Card>
          </Col>
        </Row>

        {/* AI 功能展示区 */}
        <div style={{
          marginTop: 56,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 16,
          padding: '40px 48px',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* 背景装饰 */}
          <div style={{
            position: 'absolute', right: -40, top: -40,
            width: 200, height: 200, borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)'
          }} />
          <div style={{
            position: 'absolute', right: 60, bottom: -60,
            width: 150, height: 150, borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)'
          }} />

          <Row gutter={[40, 32]} align="middle">
            <Col xs={24} md={14}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <RobotOutlined style={{ fontSize: 24, color: '#fff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>AI Teaching Assistant</div>
                  <Tag style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontSize: 11 }}>
                    OpenClaw Zero Token · No API Key Required
                  </Tag>
                </div>
              </div>
              <p style={{ fontSize: 15, opacity: 0.9, lineHeight: 1.7, marginBottom: 20 }}>
                No AI API Key purchase needed. Use your existing ChatGPT / Claude / DeepSeek account to enjoy AI-assisted teaching. Automatically analyse student results, identify weak knowledge points, and generate personalised teaching suggestions.
              </p>
              <Row gutter={[16, 12]}>
                {[
                  { icon: <ThunderboltOutlined />, text: 'Smart Score Analysis' },
                  { icon: <BarChartOutlined />, text: 'Weak Point Diagnosis' },
                  { icon: <SafetyCertificateOutlined />, text: 'Teaching Improvement Tips' },
                  { icon: <MessageOutlined />, text: 'Teaching Q&A Assistant' },
                ].map(item => (
                  <Col key={item.text} xs={12} sm={6}>
                    <div style={{
                      background: 'rgba(255,255,255,0.15)',
                      borderRadius: 10, padding: '10px 14px',
                      display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                    }}>
                      <span style={{ opacity: 0.9 }}>{item.icon}</span>
                      <span>{item.text}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>

            <Col xs={24} md={10}>
              <div style={{
                background: 'rgba(255,255,255,0.12)',
                borderRadius: 14, padding: 24, backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, opacity: 0.95 }}>
                  🚀 How to use AI for free?
                </div>
                {[
                  { n: 1, text: 'Download & install OpenClaw Zero Token', sub: 'Open-source free tool on GitHub' },
                  { n: 2, text: 'Log in to any AI site in Chrome', sub: 'DeepSeek / Kimi / ChatGPT …' },
                  { n: 3, text: 'Open Teacher Portal → click "AI Assistant"', sub: 'Enter the local URL and start' },
                ].map(item => (
                  <div key={item.n} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                      background: 'rgba(255,255,255,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700
                    }}>{item.n}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{item.text}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{item.sub}</div>
                    </div>
                  </div>
                ))}
                <Button
                  block
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate('/teacher')}
                  style={{
                    marginTop: 8, borderRadius: 8, fontWeight: 600,
                    background: '#fff', color: '#764ba2', border: 'none', height: 40
                  }}
                >
                  Try the Teacher Portal
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* 功能特性 */}
        <div style={{ marginTop: 56, textAlign: 'center' }}>
          <h2>Features</h2>
          <Row gutter={[24, 24]} style={{ marginTop: '30px' }}>
            {[
              { icon: '📄', title: 'Multiple Formats', desc: 'Support PDF, images, and Word document uploads' },
              { icon: '✏️', title: 'Online Editing', desc: 'Convenient question and answer editing features' },
              { icon: '🏷️', title: 'Knowledge Points', desc: 'Flexible knowledge point classification and tagging' },
              { icon: '📊', title: 'Smart Statistics', desc: 'Automatic grading and score statistical analysis' },
            ].map(f => (
              <Col key={f.title} xs={24} sm={12} md={6}>
                <div style={{ padding: '20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>{f.icon}</div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* Feedback Modal */}
      <Modal
        title="Share Your Feedback"
        open={feedbackVisible}
        onCancel={() => { setFeedbackVisible(false); form.resetFields(); }}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitFeedback}>
          <Form.Item name="name" label="Name (Optional)">
            <Input placeholder="Your name" />
          </Form.Item>
          <Form.Item name="email" label="Email (Optional)" rules={[{ type: 'email', message: 'Please enter a valid email address' }]}>
            <Input placeholder="your.email@example.com" />
          </Form.Item>
          <Form.Item name="feedback" label="Your Feedback" rules={[{ required: true, message: 'Please share your feedback' }]}>
            <TextArea rows={6} placeholder="Please share your thoughts, suggestions, or report any issues..." />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">Submit</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HomePage;
